// @@PORTAL_ESTIMATES_v93@@ Client portal — Estimates tab (Zoho Books estimates).
// Data: GET /api/portal/estimates?client_id= (server-scoped to this login's
// clients). Year + status filtering and sorting are client-side over that one
// list. "View" opens the estimate (line items + totals) where an awaiting
// estimate can be approved (agree + typed name) or declined (typed name +
// optional reason). The server re-checks the estimate against Zoho and the
// total shown here before anything changes. PDFs are blob fetches via apiFetch
// (a plain <a href> would skip the Bearer header).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../lib/api';
import './estimates.css';

function money(n, ccy) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: ccy || 'USD' }).format(Number(n));
  } catch (e) {
    return `$${Number(n).toFixed(2)}`;
  }
}
// YYYY-MM-DD -> local date. Never new Date('YYYY-MM-DD') (UTC -> shows the day before).
function fmtYmd(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ''));
  if (!m) return '—';
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString();
}
function fmtTs(s) { if (!s) return ''; try { return new Date(s).toLocaleString(); } catch (e) { return ''; } }
const STATUS_LABEL = {
  awaiting: 'Awaiting approval', accepted: 'Approved', declined: 'Declined', invoiced: 'Invoiced', expired: 'Expired',
};
const FILTERS = [['awaiting', 'Awaiting approval'], ['accepted', 'Approved'], ['declined', 'Declined'], ['all', 'All']];
const COLS = [
  { key: 'number', label: 'Estimate #' },
  { key: 'date', label: 'Date' },
  { key: 'expiry_date', label: 'Expires' },
  { key: 'reference', label: 'Reference' },
  { key: 'total', label: 'Total', right: true },
  { key: 'status', label: 'Status' },
];
const safeName = (s) => String(s || '').replace(/[^A-Za-z0-9._-]/g, '_');
const K = { display: 'block', fontSize: 12, color: 'var(--cp-muted)', marginBottom: 4 };

function Badge({ status }) {
  const known = !!STATUS_LABEL[status];
  return <span className={'cp-inv-badge cp-est-' + (known ? status : 'other')}>{STATUS_LABEL[status] || status || '—'}</span>;
}

export default function EstimatesTab({ clientId, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState('all');
  const [filter, setFilter] = useState(null); // null until data decides the default
  const [sort, setSort] = useState({ key: 'date', dir: 'desc' });
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [open, setOpen] = useState(null); // estimate number being viewed

  const load = useCallback(() => {
    if (!clientId) return Promise.resolve();
    setLoading(true);
    return apiFetch(`/api/portal/estimates?client_id=${encodeURIComponent(clientId)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => {
        setData(d);
        setFilter((f) => f || (d && d.awaiting > 0 ? 'awaiting' : 'all'));
      })
      .catch(() => setData({ available: false, reason: 'error' }))
      .finally(() => setLoading(false));
  }, [clientId]);

  useEffect(() => {
    setData(null); setYear('all'); setFilter(null); setMsg(''); setOpen(null);
    load();
  }, [load]);

  const ccy = (data && data.currency_code) || 'USD';

  const rows = useMemo(() => {
    if (!data || !data.available) return [];
    let list = (data.estimates || []).slice();
    if (year !== 'all') list = list.filter((i) => String(i.date || '').slice(0, 4) === year);
    if (filter === 'accepted') list = list.filter((i) => i.status === 'accepted' || i.status === 'invoiced');
    else if (filter && filter !== 'all') list = list.filter((i) => i.status === filter);
    const { key, dir } = sort;
    const mul = dir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      const av = a[key], bv = b[key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (key === 'total') return (Number(av) - Number(bv)) * mul;
      return String(av).localeCompare(String(bv), undefined, { numeric: true }) * mul;
    });
    return list;
  }, [data, year, filter, sort]);

  const total = useMemo(() => rows.reduce((t, i) => t + (Number(i.total) || 0), 0), [rows]);

  function toggleSort(key) {
    setSort((s) => (s.key === key
      ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: (key === 'number' || key === 'status' || key === 'reference') ? 'asc' : 'desc' }));
  }

  async function download(number) {
    setBusy(number); setMsg('');
    try {
      const res = await apiFetch(`/api/portal/estimates/pdf?client_id=${encodeURIComponent(clientId)}&number=${encodeURIComponent(number)}`);
      if (!res.ok) { setMsg(res.status === 404 ? `Estimate ${number} was not found.` : 'Could not download the estimate right now. Please try again shortly.'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const desig = safeName(data && data.client && data.client.designation_code);
      a.href = url; a.download = 'XYZ-Estimate-' + safeName(number) + (desig ? '-' + desig : '') + '.pdf';
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) {
      setMsg('Could not download the estimate right now. Please try again shortly.');
    } finally { setBusy(''); }
  }

  if (open) {
    return (
      <EstimateDetail clientId={clientId} number={open} clientName={data && data.client && data.client.name}
        busy={busy === open} onDownload={() => download(open)} msg={msg}
        onBack={() => { setOpen(null); setMsg(''); }}
        onDecided={() => { load(); if (onChanged) onChanged(); }} />
    );
  }

  const yearLabel = year === 'all' ? 'all years' : year;
  const filterLabel = { awaiting: 'awaiting', accepted: 'approved', declined: 'declined', all: '' }[filter || 'all'];

  return (
    <div className="cp-card">
      <div className="cp-card-head">
        <h2>Estimates</h2>
        {data && data.available && (
          <div className="cp-inv-tools">
            <select className="cp-select cp-inv-year" value={year} onChange={(e) => setYear(e.target.value)} aria-label="Year">
              <option value="all">All years</option>
              {(data.years || []).map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <div className="cp-period">
              {FILTERS.map(([id, label]) => (
                <button key={id} className={filter === id ? 'cp-pill cp-pill-on' : 'cp-pill'} onClick={() => setFilter(id)}>
                  {label}{id === 'awaiting' && data.awaiting > 0 ? ` (${data.awaiting})` : ''}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {loading && !data ? (
        <div className="cp-empty">Loading…</div>
      ) : !data || !data.available ? (
        <div className="cp-empty">
          {data && data.reason === 'not_linked'
            ? 'This account isn’t linked to billing yet.'
            : 'Estimates are temporarily unavailable. Please try again shortly.'}
        </div>
      ) : (
        <>
          <div className="cp-stmt-summary">
            <div><span className="cp-k">Estimates</span><span className="cp-v2">{rows.length}</span></div>
            <div><span className="cp-k">Total</span><span className="cp-v2">{money(total, ccy)}</span></div>
            <div><span className="cp-k">Awaiting approval</span><span className="cp-v2">{data.awaiting || 0}</span></div>
          </div>
          {msg && <div className="cp-inv-msg">{msg}</div>}
          <div className="cp-inv-scroll">
            <table className="cp-table">
              <thead>
                <tr>
                  {COLS.map((c) => (
                    <th key={c.key} className="cp-inv-th" style={c.right ? { textAlign: 'right' } : undefined}
                      onClick={() => toggleSort(c.key)} aria-sort={sort.key === c.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                      {c.label}{sort.key === c.key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                    </th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((e) => (
                  <tr key={e.number}>
                    <td>{e.number}</td>
                    <td>{fmtYmd(e.date)}</td>
                    <td>{fmtYmd(e.expiry_date)}</td>
                    <td>{e.reference || '—'}</td>
                    <td style={{ textAlign: 'right' }}>{money(e.total, ccy)}</td>
                    <td><Badge status={e.status} /></td>
                    <td style={{ textAlign: 'right' }}>
                      <div className="cp-est-actions">
                        <button className="cp-btn-sm" onClick={() => { setMsg(''); setOpen(e.number); }}>
                          {e.can_respond ? 'Review & respond' : 'View'}
                        </button>
                        <button className="cp-btn-sm cp-btn-ghost" onClick={() => download(e.number)} disabled={busy === e.number}>
                          {busy === e.number ? 'Downloading…' : 'PDF'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td className="cp-empty" colSpan={7}>No {filterLabel ? filterLabel + ' ' : ''}estimates for {yearLabel}.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {data.truncated && <p className="cp-sub" style={{ margin: '10px 20px' }}>Showing the most recent 2,000 estimates.</p>}
        </>
      )}
    </div>
  );
}

function EstimateDetail({ clientId, number, clientName, busy, onDownload, msg, onBack, onDecided }) {
  const [est, setEst] = useState(null);
  const [err, setErr] = useState('');
  const [mode, setMode] = useState('accept');
  const [agree, setAgree] = useState(false);
  const [name, setName] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [formErr, setFormErr] = useState('');
  const [done, setDone] = useState(null);

  const load = useCallback(() => {
    setErr('');
    return apiFetch(`/api/portal/estimates/detail?client_id=${encodeURIComponent(clientId)}&number=${encodeURIComponent(number)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => setEst(d.estimate))
      .catch((s) => setErr(s === 404 ? `Estimate ${number} was not found.` : 'This estimate is temporarily unavailable. Please try again shortly.'));
  }, [clientId, number]);
  useEffect(() => { setEst(null); load(); }, [load]);

  async function submit(e) {
    e.preventDefault();
    setSaving(true); setFormErr('');
    try {
      const body = { client_id: clientId, number, decision: mode, name, total: est.total };
      if (mode === 'accept') body.agree = agree; else body.reason = reason;
      const res = await apiFetch('/api/portal/estimates/decision', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        setFormErr(d.error || 'Something went wrong. Please try again.');
        if (res.status === 409) load(); // changed / no longer open -> show the current version
        return;
      }
      setDone(d.decision || { decision: d.status, name });
      setEst((x) => ({ ...x, status: d.status, can_respond: false, decision: d.decision || null }));
      if (onDecided) onDecided();
    } catch (e2) {
      setFormErr('Something went wrong. Please try again.');
    } finally { setSaving(false); }
  }

  const ccy = (est && est.currency_code) || 'USD';
  const decision = done || (est && est.decision);

  return (
    <>
      <div className="cp-card">
        <div className="cp-card-head">
          <h2>Estimate {number}</h2>
          <div className="cp-inv-tools">
            <button className="cp-btn-sm cp-btn-ghost" onClick={onBack}>← All estimates</button>
            <button className="cp-btn-sm" onClick={onDownload} disabled={busy}>{busy ? 'Downloading…' : 'Download PDF'}</button>
          </div>
        </div>
        {msg && <div className="cp-inv-msg" style={{ margin: '12px 20px 0' }}>{msg}</div>}
        {err ? <div className="cp-empty">{err}</div> : !est ? <div className="cp-empty">Loading…</div> : (
          <>
            <div className="cp-stmt-summary">
              <div><span className="cp-k">Status</span><span className="cp-v2"><Badge status={est.status} /></span></div>
              <div><span className="cp-k">Date</span><span className="cp-v2">{fmtYmd(est.date)}</span></div>
              <div><span className="cp-k">Expires</span><span className="cp-v2">{fmtYmd(est.expiry_date)}</span></div>
              <div><span className="cp-k">Total</span><span className="cp-v2">{money(est.total, ccy)}</span></div>
            </div>
            {est.reference && <p className="cp-sub" style={{ margin: '12px 20px' }}>Reference: {est.reference}</p>}
            <div className="cp-inv-scroll">
              <table className="cp-table">
                <thead>
                  <tr><th>Item</th><th style={{ textAlign: 'right' }}>Qty</th><th style={{ textAlign: 'right' }}>Rate</th><th style={{ textAlign: 'right' }}>Amount</th></tr>
                </thead>
                <tbody>
                  {(est.line_items || []).map((li, i) => (
                    <tr key={i}>
                      <td><strong>{li.name}</strong>{li.description && <div className="cp-sub" style={{ whiteSpace: 'pre-wrap' }}>{li.description}</div>}</td>
                      <td style={{ textAlign: 'right' }}>{li.quantity != null ? li.quantity : '—'}{li.unit ? ' ' + li.unit : ''}</td>
                      <td style={{ textAlign: 'right' }}>{money(li.rate, ccy)}</td>
                      <td style={{ textAlign: 'right' }}>{money(li.amount, ccy)}</td>
                    </tr>
                  ))}
                  {!(est.line_items || []).length && <tr><td className="cp-empty" colSpan={4}>See the PDF for details.</td></tr>}
                </tbody>
              </table>
            </div>
            <table className="cp-table pi-kv cp-est-totals">
              <tbody>
                {est.sub_total != null && <tr><td>Subtotal</td><td>{money(est.sub_total, ccy)}</td></tr>}
                {Number(est.discount_total) > 0 && <tr><td>Discount</td><td>-{money(est.discount_total, ccy)}</td></tr>}
                {Number(est.shipping_charge) > 0 && <tr><td>Shipping</td><td>{money(est.shipping_charge, ccy)}</td></tr>}
                {Number(est.tax_total) > 0 && <tr><td>Tax</td><td>{money(est.tax_total, ccy)}</td></tr>}
                {Number(est.adjustment) !== 0 && est.adjustment != null && <tr><td>Adjustment</td><td>{money(est.adjustment, ccy)}</td></tr>}
                <tr className="cp-est-grand"><td>Total</td><td>{money(est.total, ccy)}</td></tr>
              </tbody>
            </table>
            {(est.notes || est.terms) && (
              <div style={{ padding: '4px 20px 18px', display: 'grid', gap: 12 }}>
                {est.notes && <div><span style={K}>Notes</span><div style={{ whiteSpace: 'pre-wrap', fontSize: 14 }}>{est.notes}</div></div>}
                {est.terms && <div><span style={K}>Terms</span><div style={{ whiteSpace: 'pre-wrap', fontSize: 14 }}>{est.terms}</div></div>}
              </div>
            )}
          </>
        )}
      </div>

      {est && decision && (decision.decision === 'accepted' || decision.decision === 'declined') && (
        <div className="cp-card" style={{ borderLeft: '3px solid ' + (decision.decision === 'accepted' ? 'var(--cp-green)' : '#b42318') }}>
          <div style={{ padding: '16px 20px', fontSize: 14 }}>
            {decision.decision === 'accepted' ? 'Approved' : 'Declined'} by <strong>{decision.name}</strong>
            {decision.at ? <> on {fmtTs(decision.at)}</> : null}.
            {done && decision.decision === 'accepted' && <> Thank you — our team has been notified and will be in touch about next steps.</>}
            {done && decision.decision === 'declined' && <> Thanks for letting us know — our team has been notified.</>}
          </div>
        </div>
      )}

      {est && est.can_respond && !done && (
        <form className="cp-card" style={{ borderLeft: '3px solid ' + (mode === 'accept' ? 'var(--cp-green)' : '#b42318') }} onSubmit={submit}>
          <div className="cp-card-head">
            <h2>Respond to this estimate</h2>
            <div className="cp-period">
              <button type="button" className={mode === 'accept' ? 'cp-pill cp-pill-on' : 'cp-pill'} onClick={() => { setMode('accept'); setFormErr(''); }}>Approve</button>
              <button type="button" className={mode === 'decline' ? 'cp-pill cp-pill-on' : 'cp-pill'} onClick={() => { setMode('decline'); setFormErr(''); }}>Decline</button>
            </div>
          </div>
          <div style={{ padding: '16px 20px', display: 'grid', gap: 14 }}>
            {mode === 'accept' ? (
              <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 14, cursor: 'pointer' }}>
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} style={{ marginTop: 3 }} />
                <span>I approve estimate {est.number} for {money(est.total, ccy)}{clientName ? ` on behalf of ${clientName}` : ''}, including the notes and terms above.</span>
              </label>
            ) : (
              <label style={{ display: 'block', maxWidth: 560 }}>
                <span style={K}>Reason (optional)</span>
                <textarea className="cp-input cp-est-reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} rows={3}
                  placeholder="Let us know what you'd like changed and we can send a revised estimate." />
              </label>
            )}
            <label style={{ display: 'block', maxWidth: 420 }}>
              <span style={K}>Type your full name to sign</span>
              <input className="cp-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} autoComplete="name" />
            </label>
            {formErr && <div className="cp-inv-msg" style={{ margin: 0 }}>{formErr}</div>}
            <div>
              <button className={mode === 'accept' ? 'cp-btn-sm' : 'cp-btn-sm cp-btn-danger'} style={{ height: 40, padding: '0 20px' }} type="submit"
                disabled={saving || name.trim().length < 2 || (mode === 'accept' && !agree)}>
                {saving ? 'Sending…' : mode === 'accept' ? 'Approve estimate' : 'Decline estimate'}
              </button>
            </div>
          </div>
        </form>
      )}
    </>
  );
}
